import React from 'react';
import { useSave } from '../graphql/hooks';
import Loading from '../teal-react/components/molecules/Loading';
import SimpleTable from '../teal-react/components/organisms/SimpleTable';
import K from '../Constants';
import InlineInput from './InlineInput';
import SimpleGrid from '../teal-react/components/organisms/SimpleGrid';
import Typo from '../teal-react/components/atoms/Typo';
import Utils from '../teal-react/utils';
import { Table, TableCell, TableRow } from '@mui/material';
import ApprovedStamp from './ApprovedStamp';
import PageStrut from '../teal-react/components/atoms/PageStrut';
import LogoHeader from './layout/LogoHeader';
import SimpleCard from '../teal-react/components/organisms/SimpleCard';
import { useQcTestSummary } from '../graphql/queries';
import { useAuth } from '../teal-react/utils/token';

const BlankRow = ({ colSpan = 3 }) => {
  return (
    <TableRow>
      <TableCell colSpan={colSpan}>&nbsp;</TableCell>
    </TableRow>
  );
};

const TestingReport = ({
  qc_id,
  status,
}: {
  qc_id: number;
  status: string;
}) => {
  const { user } = useAuth();
  const targetRef = React.useRef(null);
  const [actions, setActions] = React.useState<any>(null);
  const [actionsLeft, setActionsLeft] = React.useState<any>(null);
  const saveQc = useSave('qc', 'qc');

  const READ_ONLY = Boolean(user?.isRO);

  const { data } = useQcTestSummary(qc_id);

  React.useEffect(() => {
    if (status === K.status.UNTESTED) {
      setActions(
        READ_ONLY
          ? null
          : [
              {
                label: 'Submit Test Data',
                onClick: () => {
                  saveQc({
                    id: qc_id,
                    status: K.status.PENDING,
                  });
                },
              },
            ]
      );
      setActionsLeft(
        READ_ONLY
          ? null
          : [
              {
                color: 'error',
                label: 'Cancel Batch',
                onClick: () => {
                  saveQc({
                    id: qc_id,
                    status: K.status.CANCELLED,
                  });
                },
              },
            ]
      );
    } else if (status === K.status.PENDING) {
      setActions(
        READ_ONLY
          ? null
          : [
              {
                label: 'Submit Approval',
                onClick: () => {
                  saveQc({
                    id: qc_id,
                    status: K.status.APPROVED,
                  });
                },
              },
            ]
      );
    } else {
      setActions(null);
      setActionsLeft(null);
    }
    // eslint-disable-next-line
  }, [data, status]);

  if (!data) {
    return <Loading />;
  }
  const qc = data.qc[0];
  return (
    <div ref={targetRef}>
      <SimpleCard>
        <div style={{ padding: '0 20px 10px 20px' }}>
          <SimpleGrid
            spacing={1}
            items={[
              {
                content: <LogoHeader />,
                size: 12,
              },
              {
                content: Typo.batch_header('Product #', qc.product.code),
                size: 6,
              },
              {
                content: Typo.batch_header(
                  'Date',
                  Utils.date.formatDate(qc.date) || ''
                ),
                size: 6,
              },
              {
                content: Typo.batch_header('Product', qc.product.name),
                size: 6,
              },
              {
                content: Typo.batch_header('QC', qc.qc_code),
                size: 6,
              },

              {
                content: Typo.batch_header(
                  'Quantity',
                  `${qc.amount || ''} ${qc.unit?.name || ''}`
                ),
                size: 6,
              },
            ]}
          />
        </div>
        <SimpleTable
          noGutter
          header={<div className="tc">TESTING REPORT</div>}
          data={data.qcTestSummary}
          actionsRight={actions}
          actionsLeft={actionsLeft}
          columns={[
            {
              header: 'Testing',
              cell: 'name',
              sort: false,
            },
            {
              header: 'Specification',
              sort: false,
              cell: (r: any) => {
                if (
                  !READ_ONLY &&
                  status === K.status.PENDING &&
                  r.name !== 'Remark'
                ) {
                  return (
                    <InlineInput
                      value={r.parameter}
                      table="qc_test"
                      field="test_parameter_override"
                      refresh="qcTestSummary"
                      id={r.qc_test_id}
                    />
                  );
                } else {
                  return r.parameter;
                }
              },
            },
            {
              header: 'Data',
              sort: false,
              cell: (r: any) => {
                if (!READ_ONLY && status === K.status.UNTESTED) {
                  return (
                    <InlineInput
                      value={r.result}
                      table="qc_test"
                      field="result"
                      refresh="qcTestSummary"
                      id={r.qc_test_id}
                    />
                  );
                } else {
                  return r.result;
                }
              },
            },
          ]}
          manual_bottom_rows={
            <>
              <BlankRow />
              <BlankRow />

              <TableRow>
                <TableCell>&nbsp;</TableCell>
                <td rowSpan={4}>
                  {qc.status === K.status.APPROVED ? <ApprovedStamp /> : null}
                </td>
              </TableRow>
              <BlankRow colSpan={1} />
              <BlankRow colSpan={1} />
              <BlankRow colSpan={1} />
              <BlankRow colSpan={1} />
            </>
          }
        />
        <PageStrut parentRef={targetRef} id={`TR${qc_id}`} />
        <Table className="plusco-footer-table">
          <tbody>
            <tr>
              <td>ID</td>
              <td>TITLE</td>
              <td>DOCUMENT TYPE</td>
              <td>REV #</td>
              <td>APP</td>
              <td>PAGE</td>
            </tr>
            <tr>
              <td>QAF:2</td>
              <td>BATCH TESTING FORM</td>
              <td>FORM OR EXHIBIT</td>
              <td>1-9-3-12</td>
              <td>RD</td>
              <td>1 OF 1</td>
            </tr>
          </tbody>
        </Table>
      </SimpleCard>
    </div>
  );
};

export default TestingReport;
